/**
 * The two pure decisions behind the contacts table's pagination control.
 *
 * Their own module so they can be tested without mounting the page: both are
 * arithmetic over page numbers, which is where off-by-ones live, and neither
 * needs a store, a query or a DOM to be wrong.
 */

/** A page button, or the gap between two runs of them. */
export type PageEntry = number | '…';

/**
 * Which page buttons to render.
 *
 * Every page up to seven; beyond that the first, the last, and a window around
 * the current one, with a gap marking what was left out. Ported from the mock's
 * `pageList`, so the control reads the way the approved design does.
 */
export function pageList(current: number, pageCount: number): PageEntry[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);

  const pages: PageEntry[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(pageCount - 1, current + 1);

  if (start > 2) pages.push('…');
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < pageCount - 1) pages.push('…');
  pages.push(pageCount);

  return pages;
}

/**
 * "26–50 of 999".
 *
 * Reads the real total rather than the length of the loaded page, so the last
 * page says "976–999 of 999" instead of claiming a full page of rows.
 */
export function range(page: number, pageSize: number, total: number): string {
  if (total === 0) return 'none';

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return `${first}–${last} of ${total}`;
}
